import PropTypes from 'prop-types';
import { AudioPlayer, ButtonTouch, Textarea } from '@definedcrowd/Uicomponents';
import { get } from 'lodash';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { colors, Elevation } from '../../utils';
import {
  completeTask as completeTaskAction,
  getCallUrl as getCallUrlAction
} from '../../lib/redux';
import {
  FEEDBACK_DETAIL_OPTIONS as DETAILS,
  FEEDBACK_OPTIONS,
  COMPLETE_TASK_RESULT
} from '../../Constants';
import AllGoodIconColor from '../../assets/images/call-feedback-all-good-color.svg';
import DishonestContributorIconColor from '../../assets/images/call-feedback-dishonest-contributor-color.svg';
import ContentIssueIconColor from '../../assets/images/call-feedback-content-issues-color.svg';
import TechnicalIssueIconColor from '../../assets/images/call-feedback-technical-issues-color.svg';
import AllGoodIconGrey from '../../assets/images/call-feedback-all-good-grey.svg';
import DishonestContributorIconGrey from '../../assets/images/call-feedback-dishonest-contributor-grey.svg';
import ContentIssueIconGrey from '../../assets/images/call-feedback-content-issue-grey.svg';
import TechnicalIssueIconGrey from '../../assets/images/call-feedback-technical-issues-grey.svg';
import {
  Button,
  Card as BaseCard,
  Typography,
  H2,
  Hints,
  Alert as BaseAlert,
  Radio,
  CompletedCallModal,
  RoundNavigation,
  SessionIdleModal
} from '../../components';
import { JobBanner } from '../../components/JobBanner';
import { setUnavailable } from '../../lib/taskrouter';
import { getJobAgentHits as getJobAgentHitsAction } from '../../lib/redux/actions/me';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
`;

const AudioContainer = styled.div`
  border-bottom: 1px ${colors.g03} dashed;
  padding-bottom: 28px;
  margin-bottom: 40px;
`;

const TextareaContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
  margin-top: 32px;
  width: 100%;

  .control-textarea__countdown {
    color: ${colors.g04};

    &--overpass {
      color: ${colors.r01};
    }
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  justify-content: space-around;
`;

const Option = styled.div`
  width: 134px;
  .dcComp-button-touch {
    height: 117px;
    span {
      font-size: 16px;
      color: ${colors.g04};
    }
  }
  .dcComp-button-touch:disabled span {
    color: ${colors.g02};
  }
`;

const Alert = styled(BaseAlert)`
  left: 0;
  right: 0;
  top: 72px;
  z-index: ${Elevation.Alert};
`;

const Card = styled(BaseCard)`
  align-items: initial;
`;

const OptionDetailsWrapper = styled.div`
  margin-top: 40px;
`;

const OptionDetailContainer = styled.div`
  align-items: center;
  background-color: ${colors.g01};
  border-radius: 4px;
  display: flex;
  flex: 1;
  flex-direction: row;
  height: 40px;
  margin-bottom: 8px;
  padding-left: 16px;
`;

const RequiredCommentIcon = styled.span`
  color: ${colors.b01};
`;

const feedbackOptions = [
  {
    value: FEEDBACK_OPTIONS.OK,
    label: 'All Good!'
  },
  {
    value: FEEDBACK_OPTIONS.DISHONEST_CONTRIBUTOR,
    label: 'Dishonest Contributor',
    items: [
      { label: 'Non-native', value: DETAILS.NON_NATIVE },
      {
        label: "Didn't pick up the phone / wasn't ready to execute",
        value: DETAILS.NOT_READY_TO_EXECUTE
      },
      {
        label: 'Received an automated message or voicemail',
        value: DETAILS.VOICEMAIL
      },
      {
        label: "Environment didn't correspond to the guidelines (silent, noisy)",
        value: DETAILS.BAD_ENVIRONMENT
      },
      { label: 'Other', value: DETAILS.OTHER_DISHONEST_CONTRIBUTOR }
    ]
  },
  {
    value: FEEDBACK_OPTIONS.CONTENT_ISSUE,
    label: 'Content Issue(s) (Script)',
    items: [
      {
        label: 'Incoherent scenario for the domain or the region',
        value: DETAILS.INCOHERENT_SCENARIO
      },
      { label: 'The script too short or too long', value: DETAILS.INAPPROPRIATE_SCRIPT },
      { label: 'Mismatch scripts', value: DETAILS.MISMATCH_SCRIPTS },
      { label: 'Other', value: DETAILS.OTHER_CONTENT_ISSUE }
    ]
  },
  {
    value: FEEDBACK_OPTIONS.TECHNICAL_ISSUE,
    label: 'Technical Issue(s)',
    items: [
      {
        label: 'The application had a glitch during the execution',
        value: DETAILS.GLITCH_DURING_EXECUTION
      },
      {
        label: 'The call has a lot of static noise/interference ',
        value: DETAILS.CALL_WITH_INTERFERENCE
      },
      {
        label: 'The status changed without human interaction',
        value: DETAILS.STATUS_CHANGED_WITHOUT_INTERACTION
      },
      {
        label: "Can't playback the recording on the feedback page",
        value: DETAILS.CANT_LISTEN_RECORDING
      },
      { label: 'Other', value: DETAILS.OTHER_TECHNICAL_ISSUE }
    ]
  }
];

function getOptionIcon(option, audioUrl) {
  switch (option) {
    case FEEDBACK_OPTIONS.OK:
      return audioUrl ? AllGoodIconColor : AllGoodIconGrey;
    case FEEDBACK_OPTIONS.TECHNICAL_ISSUE:
      return audioUrl ? TechnicalIssueIconColor : TechnicalIssueIconGrey;
    case FEEDBACK_OPTIONS.CONTENT_ISSUE:
      return audioUrl ? ContentIssueIconColor : ContentIssueIconGrey;
    case FEEDBACK_OPTIONS.DISHONEST_CONTRIBUTOR:
      return audioUrl ? DishonestContributorIconColor : DishonestContributorIconGrey;

    default:
      return null;
  }
}

function renderAlert(feedbackStatus) {
  if (feedbackStatus === COMPLETE_TASK_RESULT.RESULT_IS_NOT_VALID) {
    return (
      <Alert error danger fontWeight="bold">
        Looks like the submit button is glitching. Go ahead and try it again.
      </Alert>
    );
  }

  return null;
}

function Feedback({ history, hasAudioUrl, taskId, completeTask, getCallUrl, getJobAgentHits }) {
  const [comments, setComments] = useState('');
  const [selectedOption, setOption] = useState(FEEDBACK_OPTIONS.NONE);
  const [selectedDetail, setDetail] = useState(null);
  const [feedbackStatus, setFeedbackStatus] = useState(null);
  const [submitInProgress, setSubmitRequest] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioUrlError, setAudioUrlError] = useState(false);
  const [showTextArea, setShowTextArea] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [executedHitsCountProp, setExecutedHitsCountProp] = useState(null);
  const [maxHitsCountProp, setMaxHitsCountProp] = useState(null);

  const maxLength = 500;

  const isCommentRequired = useMemo(() => {
    const optionsOther = [
      DETAILS.OTHER_TECHNICAL_ISSUE,
      DETAILS.OTHER_DISHONEST_CONTRIBUTOR,
      DETAILS.OTHER_CONTENT_ISSUE
    ];
    return optionsOther.includes(selectedDetail && selectedDetail.value);
  }, [selectedDetail]);

  useEffect(() => {
    (async () => {
      try {
        const url = await getCallUrl(taskId);
        setAudioUrl(url);
      } catch (err) {
        setAudioUrlError(false);
      }
    })();
  }, []);

  useEffect(() => {
    setSubmitDisabled(
      submitInProgress ||
        !selectedDetail ||
        (isCommentRequired && comments.length < 10) ||
        (showTextArea && comments.length > maxLength)
    );
  }, [submitInProgress, selectedDetail, comments]);

  const submitFeedback = useCallback(() => {
    const onSubmit = async () => {
      setSubmitRequest(true);
      try {
        await setUnavailable();
        const sendComments = selectedOption.value === FEEDBACK_OPTIONS.OK ? '' : comments;
        await completeTask(taskId, selectedDetail.value, sendComments);

        const { executedHitsCount, maxHitsCount } = await getJobAgentHits(taskId, true); // get hits count using taskId instead of jobId
        setExecutedHitsCountProp(executedHitsCount);
        setMaxHitsCountProp(maxHitsCount);

        setFeedbackStatus(COMPLETE_TASK_RESULT.SUCCESS);
      } catch (err) {
        setFeedbackStatus(get(err, 'body.resultType') || COMPLETE_TASK_RESULT.RESULT_IS_NOT_VALID);
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        setSubmitRequest(false);
      }
    };

    onSubmit();
  }, [comments, selectedDetail, selectedDetail]);

  const selectOptionClick = option => {
    setOption(option);
    if (option.value === FEEDBACK_OPTIONS.OK) {
      setDetail({ value: FEEDBACK_OPTIONS.OK });
      setShowTextArea(false);
    } else {
      setShowTextArea(true);
      setDetail(null);
    }
  };

  const renderOptionDetailsWrapper = () => {
    const { items } = selectedOption;

    if (!items) return <OptionDetailsWrapper />;

    return (
      <OptionDetailsWrapper>
        <Typography margin={{ bottom: 'medium' }} textAlign="center">
          Now choose one of the options below
        </Typography>
        {items.map(item => (
          <OptionDetailContainer key={item.value}>
            <Radio
              checked={selectedDetail != null && selectedDetail.value === item.value}
              label={item.label}
              onToggle={() => setDetail(item)}
            />
          </OptionDetailContainer>
        ))}
      </OptionDetailsWrapper>
    );
  };

  const renderOptions = () => {
    return (
      <OptionsContainer>
        {feedbackOptions.map(option => (
          <Option key={option.value}>
            <ButtonTouch
              disabled={hasAudioUrl && (audioUrlError || !audioUrl)}
              useMultiColorIcon
              displayName={option.label}
              iconUrl={getOptionIcon(option.value, audioUrl)}
              selected={selectedOption.value === option.value}
              onClick={() => selectOptionClick(option)}
            />
          </Option>
        ))}
      </OptionsContainer>
    );
  };

  return (
    <>
      {renderAlert(feedbackStatus)}
      <Wrapper>
        <JobBanner />
        <Card>
          <H2 fontWeight="bold" margin={{ bottom: 'medium' }}>
            How&apos;d that call go?
          </H2>

          {hasAudioUrl && !audioUrlError && (
            <>
              <Hints color={colors.g04} margin={{ bottom: 'medium' }}>
                {audioUrl ? 'Playback the recording' : 'Loading recording...'}
              </Hints>
              <AudioContainer>
                <AudioPlayer autoPlay={false} loading={!audioUrl} audioUrl={audioUrl} />
              </AudioContainer>
            </>
          )}

          {hasAudioUrl && audioUrlError && (
            <AudioContainer>
              <Typography>Error loading audio.</Typography>
            </AudioContainer>
          )}

          <Typography margin={{ bottom: 'large' }}>
            Please select one of the following options.
          </Typography>

          {renderOptions()}

          {selectedOption !== FEEDBACK_OPTIONS.NONE && renderOptionDetailsWrapper()}

          {showTextArea && (
            <TextareaContainer>
              <Typography margin={{ bottom: 'medium' }} textAlign="center">
                Tell us more about your answer
                {isCommentRequired && <RequiredCommentIcon> *</RequiredCommentIcon>}
              </Typography>

              <Textarea
                onChange={target => setComments(target.value)}
                placeholder="Write your comments here. Try to be as descriptive as possible."
                value={comments}
                rows={7}
                maxLength={maxLength}
                enableCountdown
              />
            </TextareaContainer>
          )}

          <Button
            hidden={selectedOption === FEEDBACK_OPTIONS.NONE}
            disabled={submitDisabled}
            onClick={submitFeedback}
          >
            Submit
          </Button>
          <RoundNavigation count={2} progress={selectedOption === FEEDBACK_OPTIONS.NONE ? 0 : 1} />
        </Card>
      </Wrapper>

      {feedbackStatus === COMPLETE_TASK_RESULT.SUCCESS && (
        <CompletedCallModal
          history={history}
          executedHitsCount={executedHitsCountProp}
          maxHitsCount={maxHitsCountProp}
        />
      )}

      {feedbackStatus === COMPLETE_TASK_RESULT.ALREADY_SUBMITTED && (
        <SessionIdleModal history={history} />
      )}
    </>
  );
}

Feedback.propTypes = {
  getCallUrl: PropTypes.func.isRequired,
  hasAudioUrl: PropTypes.bool.isRequired,
  completeTask: PropTypes.func.isRequired,
  getJobAgentHits: PropTypes.func.isRequired,
  taskId: PropTypes.string,
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired
};

Feedback.defaultProps = {
  taskId: null
};

function mapStateToProps({ tasks }) {
  return {
    taskId: tasks.currentTask.task.taskId,
    hasAudioUrl: tasks.currentTask.hasAudioUrl
  };
}

export default connect(
  mapStateToProps,
  {
    completeTask: completeTaskAction,
    getJobAgentHits: getJobAgentHitsAction,
    getCallUrl: getCallUrlAction
  }
)(withRouter(Feedback));
